/**
 * One-screen best/worst mode for max_diff (spec 36-03).
 *
 * Two rails: the side a control sits on IS the side it means. This replaces a tap cycle whose one
 * gesture meant best, then worst, then clear — and meant worst outright once the best quota filled.
 * A shipped hint line tried to explain that mode and did not fix it; the mode is gone instead.
 *
 * Carries its OWN Next button rather than the shared bottom one, whose `canProceed` is keyed on the
 * legacy `{most_appealing, least_appealing}` shape. `SurveySession.tsx` hides the shared button for
 * this mode so the two affordances never both render.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT } from '../../config/ui-text';
import {
  Anchors,
  BestWorstSide,
  BestWorstStage,
  BestWorstState,
  Quotas,
  advanceStage,
  emptyState,
  isComplete,
  nextAsk,
  setSide,
  toResponseValue,
} from './bestWorst';

// The whole row takes the tint, not just the control (CEO, 2026-09-02) — the light green and red
// the shipped card used. `row` is the fill; `on` is the control sitting inside it.
const SIDE_STYLES: Record<BestWorstSide, { on: string; row: string; header: string }> = {
  best: {
    on: 'bg-green-500 border-green-500 text-white',
    row: 'border-green-500 bg-green-50 text-green-900',
    header: 'text-green-700',
  },
  worst: {
    on: 'bg-red-500 border-red-500 text-white',
    row: 'border-red-500 bg-red-50 text-red-900',
    header: 'text-red-900',
  },
};

// Both quotas 0: the question asks for nothing, so there is no rail, no row and nothing to pick.
// Saying so beats a blank card under "That's everything" — `isComplete` refuses to record it as
// answered, so the respondent would otherwise be looking at an empty screen with no explanation.
const NOTHING_ASKED = 'This question isn’t set up yet — there’s nothing to choose from.';

const ROW_BASE = 'flex items-center gap-3 rounded-lg border-2 px-3 py-2 transition-colors';
const ROW_OFF = 'border-gray-200 bg-white text-gray-900';

const KNOB_BASE =
  'w-11 h-11 flex-none rounded-lg border-2 inline-flex items-center justify-center ' +
  'font-semibold text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed';
const KNOB_OFF = 'border-gray-300 bg-white';
// The side being asked for right now: its empty controls ARE the affordance, so they carry that
// side's colour instead of a neutral ring the respondent has to decode from the heading.
const KNOB_CUE: Record<BestWorstSide, string> = {
  best: 'border-green-300 bg-green-50',
  worst: 'border-red-300 bg-red-50',
};
const KNOB_IDLE = 'opacity-45';
// A square, not a circle: a ring reads as a radio button, and a radio button means "pick one"
// when the respondent is being asked for three.
const EMPTY_MARK = 'w-[15px] h-[15px] rounded border-2 border-gray-300';

const Pips: React.FC<{ filled: number; total: number }> = ({ filled, total }) => (
  <span className="inline-flex gap-1" aria-hidden="true">
    {Array.from({ length: total }, (_, i) => (
      <span
        key={i}
        className={`w-1.5 h-1.5 rounded-full border border-current ${
          i < filled ? 'bg-current' : 'opacity-40'
        }`}
      />
    ))}
  </span>
);

/**
 * The paint-tracking `SurveySession` already owns for every question type, handed down rather than
 * rebuilt here — a second copy of that state is a second copy that drifts.
 */
export interface BestWorstMediaGate {
  imageUrlFor: (option: string) => string | undefined;
  keyFor: (option: string) => string;
  loaded: Record<string, boolean>;
  errored: Record<string, boolean>;
  timedOut: boolean;
  onPainted: (key: string) => void;
  onLoaded: (key: string) => void;
  onErrored: (key: string) => void;
}

export interface BestWorstCardProps {
  options: string[];
  nBest: number;
  nWorst: number;
  anchors: Anchors;
  media?: BestWorstMediaGate;
  onSubmit: (value: ReturnType<typeof toResponseValue>) => void;
}

export const BestWorstCard: React.FC<BestWorstCardProps> = ({
  options,
  nBest,
  nWorst,
  anchors,
  media,
  onSubmit,
}) => {
  /**
   * The picks and the stage move together in ONE value: the stage is a function of the picks, and
   * as two states a fast second tap read a stale one. Forward only, because counts alone cannot
   * tell "not started" from "finished, then moved one across" — ADR-0003 §5.
   */
  const [picks, setPicks] = useState<{ state: BestWorstState; stage: BestWorstStage }>(() => {
    // Seeded THROUGH `advanceStage`, not parked on 'best'. The stage otherwise only ever moved
    // inside a pick, so a legal one-sided exercise with `n_best: 0` opened asking for "the 0 you'd
    // find most appealing" — a side that is not even rendered — with every control on the side
    // that IS rendered greyed and every row tap refused.
    const seed = emptyState();
    return { state: seed, stage: advanceStage(seed, { nBest, nWorst }, 'best') };
  });
  const { state, stage } = picks;
  const quotas: Quotas = { nBest, nWorst };
  const complete = isComplete(state, quotas);
  const ask = nextAsk(state, quotas, stage);
  /** The side a row tap answers; null at review — ADR-0003 addendum. */
  const askedSide: BestWorstSide | null = ask.phase === 'review' ? null : ask.phase;

  /**
   * Whether the ask just moved on, so it can say so — the explaining words sit at the top of the
   * card and are off-screen on a long list. Never on first paint, and keyed on the instruction
   * rather than the counter so counting down within one side does not spend the signal.
   */
  const askKey = `${ask.phase}:${ask.kind === 'refill' ? ask.kind : 'asking'}`;
  const seenAsk = useRef<string | null>(null);
  const askChanged = seenAsk.current !== null && seenAsk.current !== askKey;
  useEffect(() => {
    seenAsk.current = askKey;
  }, [askKey]);

  const choose = (option: string, side: BestWorstSide) =>
    setPicks((prev) => {
      const next = setSide(prev.state, option, side, quotas);
      return { state: next, stage: advanceStage(next, quotas, prev.stage) };
    });

  const quotaFor = (side: BestWorstSide) => (side === 'best' ? nBest : nWorst);
  const labelFor = (side: BestWorstSide) => (side === 'best' ? anchors.best : anchors.worst);
  // A quota of 0 is a legal one-sided exercise (spec-02), so a rail is only rendered when it can
  // actually be used — two rails must not assume two sides.
  const sides: BestWorstSide[] = (['best', 'worst'] as BestWorstSide[]).filter(
    (s) => quotaFor(s) > 0,
  );

  // A respondent must not rank packaging they never saw — the same hazard the legacy MaxDiff path
  // gates for. Every slot on screen, not just its own row: a card where one pack shot is still in
  // flight is a card where the comparison is not yet the one being asked for. Fails open on the
  // gate's own timeout so a dead CDN cannot strand anyone mid-survey.
  const imagesReady =
    !media ||
    media.timedOut ||
    options.every((o) => !media.imageUrlFor(o) || media.loaded[media.keyFor(o)]);

  /** A row tap is the asked side's knob by another name, so it is refused where that knob is. */
  const rowTapBlocked = (option: string, side: BestWorstSide) =>
    !state[side].includes(option) && state[side].length >= quotaFor(side);

  const knob = (option: string, side: BestWorstSide) => {
    const rank = state[side].indexOf(option);
    const on = rank >= 0;
    const full = state[side].length >= quotaFor(side);
    return (
      <button
        key={side}
        type="button"
        aria-pressed={on}
        aria-label={`${on ? 'Remove' : 'Mark'} "${option}" as ${labelFor(side).toLowerCase()}`}
        disabled={!imagesReady || (!on && full)}
        onClick={() => choose(option, side)}
        className={`${KNOB_BASE} ${
          on
            ? SIDE_STYLES[side].on
            : ask.phase === side
              ? KNOB_CUE[side]
              : `${KNOB_OFF} ${KNOB_IDLE}`
        }`}
      >
        {on ? rank + 1 : <span className={EMPTY_MARK} />}
      </button>
    );
  };

  /** Which side an option is on, so the row can carry that side's fill. */
  const sideOf = (option: string): BestWorstSide | null =>
    state.best.includes(option) ? 'best' : state.worst.includes(option) ? 'worst' : null;

  return (
    <div className="space-y-6">
      <div
        data-testid="best-worst-ask"
        data-ask-changed={askChanged || undefined}
        className={`rounded-md -mx-2 px-2 py-1.5 ${
          askChanged ? 'motion-safe:animate-askMoved' : ''
        }`}
      >
        {sides.length === 0 ? (
          <p className="text-[15px] font-semibold text-gray-900">{NOTHING_ASKED}</p>
        ) : ask.phase === 'review' ? (
          <>
            <p className="text-[15px] font-semibold text-gray-900">
              That&rsquo;s everything — change any of it if you like.
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {/* Both sides are full here, so a move onto one is refused outright. */}
              Tap a filled control to take it off, then pick again.
            </p>
          </>
        ) : (
          <>
            <p className="text-[15px] font-semibold text-gray-900">
              {ask.kind === 'start'
                ? `${ask.phase === 'best' ? 'Pick the' : 'Now pick the'} ${quotaFor(ask.phase)} you’d find `
                : `Pick ${ask.remaining} more as `}
              <span className={ask.phase === 'best' ? 'text-green-700' : 'text-red-700'}>
                {labelFor(ask.phase).toLowerCase()}
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {ask.kind === 'refill'
                ? 'Moving a pick across left a slot open.'
                : ask.phase === 'best'
                  ? `The number shows the order you pick them. You’ll do the ${labelFor('worst').toLowerCase()} next.`
                  : `Your ${labelFor('best').toLowerCase()} picks stay below — to move one here, tap its ${labelFor('worst').toLowerCase()} control.`}
            </p>
          </>
        )}
      </div>

      <div className="flex items-center justify-between border-b border-gray-200 pb-2 text-xs font-semibold uppercase tracking-wide">
        {sides.map((side) => (
          <span key={side} className={`flex items-center gap-2 ${SIDE_STYLES[side].header}`}>
            {side === 'worst' && <Pips filled={state[side].length} total={quotaFor(side)} />}
            {labelFor(side)}
            {side === 'best' && <Pips filled={state[side].length} total={quotaFor(side)} />}
          </span>
        ))}
      </div>

      <div className="space-y-2">
        {options.map((option) => (
          <div
            key={option}
            className={`${ROW_BASE} ${sideOf(option) ? SIDE_STYLES[sideOf(option)!].row : ROW_OFF}`}
          >
            {sides.includes('best') ? knob(option, 'best') : null}
            {media?.imageUrlFor(option) && (
              <img
                // Keyed by the media key, not the row: without an identity of its own this node is
                // handed to a different option on a re-render and shows the previous decoded frame.
                key={media.keyFor(option)}
                src={media.imageUrlFor(option)}
                alt={option}
                className="w-14 h-14 flex-none rounded-md object-cover bg-gray-100"
                onLoad={() => media.onPainted(media.keyFor(option))}
                onError={() => {
                  // Resolves the gate as well as recording the failure, so a dead image fails open
                  // instead of locking the respondent out of the question.
                  media.onErrored(media.keyFor(option));
                  media.onLoaded(media.keyFor(option));
                }}
              />
            )}
            {askedSide === null ? (
              <span className="flex-1 min-w-0">{option}</span>
            ) : (
              <button
                type="button"
                /* The widest target on the row; silence there reads as a frozen page. No tab
                   stop and hidden: both knobs already announce this option — ADR-0003 addendum. */
                tabIndex={-1}
                aria-hidden="true"
                disabled={!imagesReady || rowTapBlocked(option, askedSide)}
                onClick={() => choose(option, askedSide)}
                className="flex-1 min-w-0 text-left disabled:cursor-default"
              >
                {option}
              </button>
            )}
            {sides.includes('worst') ? knob(option, 'worst') : null}
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!complete}
          onClick={() => onSubmit(toResponseValue(state))}
          icon={<Check className="w-4 h-4" />}
        >
          {UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.NEXT}
        </Button>
      </div>
    </div>
  );
};

export default BestWorstCard;
